import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    TableCell,
    TableHead,
    TableRow,
    TableBody,
    CircularProgress,
    Chip,
    IconButton,
    Switch,
    Snackbar,
    Alert
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { AppTable } from '../../../shared/components/AppTable';
import { getCategories, Category, CategoryValue } from '../../../shared/services/categoryService';
import { toggleTopRated } from '../../../shared/services/categoryValueService';

export const CategoryValuesList: React.FC = () => {
    const { categoryName } = useParams<{ categoryName: string }>();
    const navigate = useNavigate();
    const [category, setCategory] = useState<Category | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [toggling, setToggling] = useState<number | null>(null);

    useEffect(() => {
        loadCategoryDetails();
    }, [categoryName]);

    const loadCategoryDetails = async () => {
        try {
            setLoading(true);
            const response = await getCategories({ search: categoryName });
            let categories: Category[] = [];
            if (response.data) {
                if (Array.isArray(response.data)) {
                    categories = response.data;
                } else if ('items' in response.data) {
                    categories = response.data.items;
                }
            }
            const found = categories.find((cat: Category) => cat.catName === categoryName);

            if (found) {
                setCategory(found);
            } else {
                setError('Category not found');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load category details');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/category');
    };

    const handleToggleTopRated = async (value: CategoryValue) => {
        const id = value.id || value.categoryValueId.toString();
        const originalStatus = !!value.isTopRated;

        try {
            // Optimistic update
            if (category) {
                const updatedValues = category.categoryValues?.map(v =>
                    v.categoryValueId === value.categoryValueId ? { ...v, isTopRated: !originalStatus } : v
                );
                setCategory({ ...category, categoryValues: updatedValues });
            }

            const response = await toggleTopRated(id);
            if (response.success) {
                if (category) {
                    const updatedValues = category.categoryValues?.map(v =>
                        v.categoryValueId === value.categoryValueId
                            ? { ...v, isTopRated: response.data?.isTopRated ?? !originalStatus }
                            : v
                    );
                    setCategory({ ...category, categoryValues: updatedValues });
                    setSuccess(response.message || 'Status updated successfully');
                }
            } else {
                // Rollback if server returns error
                if (category) {
                    const updatedValues = category.categoryValues?.map(v =>
                        v.categoryValueId === value.categoryValueId ? { ...v, isTopRated: originalStatus } : v
                    );
                    setCategory({ ...category, categoryValues: updatedValues });
                }
                setError(response.message || 'Failed to update status');
            }
        } catch (err: any) {
            // Rollback on catch
            if (category) {
                const updatedValues = category.categoryValues?.map(v =>
                    v.categoryValueId === value.categoryValueId ? { ...v, isTopRated: originalStatus } : v
                );
                setCategory({ ...category, categoryValues: updatedValues });
            }
            setError(err.response?.data?.message || 'Failed to toggle Top Rated status');
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col flex-auto min-w-0">
                {/* Header - same as loaded state */}
                <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                    <div className="flex items-center gap-4">
                        <IconButton
                            onClick={handleBack}
                            sx={{
                                color: '#0EA5E9',
                                '&:hover': {
                                    backgroundColor: 'rgba(14, 165, 233, 0.1)',
                                },
                            }}
                        >
                            <ArrowBackIcon fontSize="large" />
                        </IconButton>
                        <div>
                            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight leading-tight" style={{ color: '#0EA5E9' }}>
                                Loading...
                            </h2>
                            <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                                Fetching category values
                            </div>
                        </div>
                    </div>
                </div>
                {/* Loader content */}
                <div className="flex flex-auto p-6 sm:p-10">
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        width="100%"
                        minHeight="400px"
                        sx={{
                            backgroundColor: '#fafafa',
                            borderRadius: '12px',
                            border: '1px dashed #e0e0e0',
                        }}
                    >
                        <CircularProgress
                            size={56}
                            thickness={4}
                            sx={{
                                color: '#0EA5E9',
                                marginBottom: 2,
                            }}
                        />
                        <Box component="span" sx={{ fontSize: '16px', color: '#666', fontWeight: 500 }}>
                            Loading category values...
                        </Box>
                        <Box component="span" sx={{ fontSize: '14px', color: '#999', marginTop: 0.5 }}>
                            Please wait
                        </Box>
                    </Box>
                </div>
            </div>
        );
    }

    if (error || !category) {
        return (
            <div className="flex flex-col flex-auto min-w-0">
                <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card">
                    <div className="flex items-center gap-4">
                        <IconButton onClick={handleBack}>
                            <ArrowBackIcon />
                        </IconButton>
                        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                            Error
                        </h2>
                    </div>
                </div>
                <div className="flex flex-auto p-6 sm:p-10">
                    <div style={{ color: 'red' }}>{error || 'Category not found'}</div>
                </div>
            </div>
        );
    }

    const values = category.categoryValues || [];

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex items-center gap-4">
                    <IconButton
                        onClick={handleBack}
                        sx={{
                            color: '#0EA5E9',
                            '&:hover': {
                                backgroundColor: 'rgba(14, 165, 233, 0.1)',
                            },
                        }}
                    >
                        <ArrowBackIcon fontSize="large" />
                    </IconButton>
                    <div>
                        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight" style={{ color: '#0EA5E9' }}>
                            {category.catName}
                        </h2>
                        <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                            {category.displayName && `${category.displayName} | `}Total Values: {values.length}
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {values.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Paper sx={{ padding: '40px', textAlign: 'center' }}>
                                <div style={{ fontSize: '18px', color: '#999' }}>
                                    No category values found
                                </div>
                            </Paper>
                        </Box>
                    ) : (
                        <AppTable
                            head={
                                <TableRow>
                                    <TableCell sx={{ backgroundColor: '#0EA5E9', color: 'white', fontWeight: 'bold' }}>
                                        ID
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: '#0EA5E9', color: 'white', fontWeight: 'bold' }}>
                                        Value Name
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: '#0EA5E9', color: 'white', fontWeight: 'bold' }}>
                                        Parent ID
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: '#0EA5E9', color: 'white', fontWeight: 'bold' }}>
                                        Applies Online
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: '#0EA5E9', color: 'white', fontWeight: 'bold' }}>
                                        Description
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: '#0EA5E9', color: 'white', fontWeight: 'bold' }}>
                                        Sort Order
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: '#0EA5E9', color: 'white', fontWeight: 'bold' }}>
                                        Sync Ts
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: '#0EA5E9', color: 'white', fontWeight: 'bold' }}>
                                        Status
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: '#0EA5E9', color: 'white', fontWeight: 'bold' }}>
                                        LOV Sequence
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: '#0EA5E9', color: 'white', fontWeight: 'bold' }}>
                                        Top Rated
                                    </TableCell>
                                </TableRow>
                            }
                            isEmpty={false}
                            emptyColSpan={9}
                            stickyHeader
                            className="!rounded-lg !shadow-md"
                        >
                            {values.map((value, index) => (
                                <TableRow
                                    key={index}
                                    hover
                                    sx={{
                                        '&:nth-of-type(odd)': {
                                            backgroundColor: '#f9f9f9',
                                        },
                                        '&:hover': {
                                            backgroundColor: '#f0f9ff !important',
                                        },
                                    }}
                                >
                                    <TableCell>{value.categoryValueId}</TableCell>
                                    <TableCell>
                                        <strong style={{ color: '#0EA5E9' }}>
                                            {value.categoryValueName}
                                        </strong>
                                    </TableCell>
                                    <TableCell>{value.parentId}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={value.appliesOnline === 1 ? 'Yes' : 'No'}
                                            color={value.appliesOnline === 1 ? 'primary' : 'default'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>{value.description || '-'}</TableCell>
                                    <TableCell>{value.sortOrder}</TableCell>
                                    <TableCell>{value.syncTs}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={value.catStatus === 'Y' ? 'Active' : 'Inactive'}
                                            color={value.catStatus === 'Y' ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{value.lovSequence}</TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={!!value.isTopRated}
                                            onChange={() => handleToggleTopRated(value)}
                                            disabled={toggling === value.categoryValueId}
                                            color="primary"
                                            size="small"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </AppTable>
                    )}
                </div>
            </div>
            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={() => { setError(''); setSuccess(''); }}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
